"use client";

import { cn } from "@repo/ui/lib/utils";
import { useEffect, useState } from "react";

import { api } from "@/lib/trpc/react";

interface Preview {
  url: string;
  siteName?: string;
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
}

/**
 * Fetched once per url for the life of the tab.
 *
 * A module-level cache rather than react-query's, because the endpoint is a
 * mutation: a link repeated across a thread -- which is exactly what an
 * opportunity link is -- would otherwise be fetched once per bubble.
 *
 * Two maps, not one. `resolved` holds answers, including "this link has no
 * preview"; `inFlight` holds the promise for a request already out. Collapsing
 * them and marking the cache before the fetch is what broke this: React runs an
 * effect twice in development, the second pass read the marker as an answer and
 * stopped, and the real one arrived after the first pass had already given up
 * its subscription. Sharing the promise lets every caller wait on one request
 * and still hear the result.
 */
const resolved = new Map<string, Preview | null>();
const inFlight = new Map<string, Promise<Preview | null>>();

/**
 * The card under a message carrying a link.
 *
 * Renders nothing at all until it has something worth showing: no skeleton and
 * no error state. A preview is an enrichment, and a thread that flashes an
 * empty box under every url reads worse than one that simply shows the link.
 */
export function LinkPreview({
  url,
  ownSide,
}: {
  url: string;
  /**
   * Whether the bubble this card belongs to is on the reader's own side. Not
   * "did we send it": the widget puts the expert on that side and the internal
   * viewer puts us there, and a card that answered the second question landed
   * opposite the message it belongs to.
   */
  ownSide: boolean;
}) {
  const [preview, setPreview] = useState<Preview | null>(
    () => resolved.get(url) ?? null,
  );
  const fetchPreview = api.util.getOpenGraphPreview.useMutation();
  const { mutateAsync } = fetchPreview;

  useEffect(() => {
    if (resolved.has(url)) {
      setPreview(resolved.get(url) ?? null);
      return;
    }
    let live = true;
    let request = inFlight.get(url);
    if (!request) {
      request = mutateAsync({ url })
        .then((result) => result.data ?? null)
        // A link we cannot read is still a link. Nothing to say about it.
        .catch(() => null)
        .then((data) => {
          resolved.set(url, data);
          inFlight.delete(url);
          return data;
        });
      inFlight.set(url, request);
    }
    void request.then((data) => {
      if (live) {
        setPreview(data);
      }
    });
    return () => {
      live = false;
    };
  }, [url, mutateAsync]);

  // A card with no title and no image says less than the link already does.
  if (!preview?.title && !preview?.image) {
    return null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        // `shrink-0` is load-bearing: the card is a flex child in the message
        // column, and a flex child shrinks by default -- without it the box is
        // squeezed to a fraction of its content and `overflow-hidden` eats the
        // title.
        "border-border bg-muted/40 hover:bg-muted mb-4 block w-[15rem] shrink-0 overflow-hidden rounded-[14px] border no-underline transition-colors",
        ownSide ? "self-end" : "self-start",
      )}
    >
      {preview.image ? (
        // eslint-disable-next-line @next/next/no-img-element -- an arbitrary
        // remote host, which the optimizer has no remotePattern for
        <img
          src={preview.image}
          alt=""
          className="block h-[7.5rem] w-full object-cover"
          // A card whose image 404s should lose the image, not the card.
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <div className="flex flex-col gap-0.5 px-3 py-2.5">
        {preview.siteName ? (
          <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
            {preview.siteName}
          </span>
        ) : null}
        {preview.title ? (
          <span className="text-foreground line-clamp-2 text-[13px] leading-snug font-medium">
            {preview.title}
          </span>
        ) : null}
        {preview.description ? (
          <span className="text-muted-foreground line-clamp-2 text-[12px] leading-snug">
            {preview.description}
          </span>
        ) : null}
      </div>
    </a>
  );
}
